/**
 * Grace Period Banner Component (Story 1.7)
 * Displays warning banner when user is in grace period for 2FA.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAuthStore } from "../../store/authStore";
import { useTwoFactorStore } from "../../store/twoFactorStore";

export function GracePeriodBanner() {
  const { t } = useTranslation("common");
  const [dismissed, setDismissed] = useState(false);
  const { totpDisabledByAdmin } = useAuthStore();
  const { status, fetchStatus } = useTwoFactorStore();

  // Fetch 2FA status on mount to get latest grace period info
  useEffect(() => {
    fetchStatus().catch(() => {});
  }, [fetchStatus]);

  // Don't show if 2FA is already enabled
  if (status?.enabled) {
    return null;
  }

  // Wait for live status before deciding visibility to avoid stale persisted grace values.
  if (status === null) {
    return null;
  }

  // Don't show if not in grace period
  if (!status.gracePeriodStarted) {
    return null;
  }

  // Don't show if dismissed (per session)
  if (dismissed) {
    return null;
  }

  const daysRemaining = status.graceDaysRemaining ?? 0;
  const isExpired = daysRemaining === 0;
  const isUrgent = daysRemaining <= 2;

  return (
    <div
      className={`
        flex items-center justify-between gap-4 px-4 py-3
        ${
          isExpired
            ? "bg-danger-100 dark:bg-danger-900/30 border-b border-danger-200 dark:border-danger-800"
            : isUrgent
              ? "bg-warning-100 dark:bg-warning-900/30 border-b border-warning-200 dark:border-warning-800"
              : "bg-primary-100 dark:bg-primary-900/30 border-b border-primary-200 dark:border-primary-800"
        }
      `}
    >
      <div className="flex items-center gap-3">
        <ExclamationTriangleIcon
          className={`
            w-5 h-5 flex-shrink-0
            ${
              isExpired
                ? "text-danger-600 dark:text-danger-400"
                : isUrgent
                  ? "text-warning-600 dark:text-warning-400"
                  : "text-primary-600 dark:text-primary-400"
            }
          `}
        />
        <p
          className={`
            text-sm
            ${
              isExpired
                ? "!text-danger-800 dark:!text-danger-500"
                : isUrgent
                  ? "!text-warning-800 dark:!text-warning-500"
                  : "!text-primary-800 dark:!text-primary-500"
            }
          `}
        >
          {totpDisabledByAdmin ? (
            <>
              <strong>{t("twofa_disabled_by_admin")}</strong> {t("twofa_reenable_prompt")}
              {!isExpired && (
                <> {t("twofa_grace_period_day", { count: daysRemaining })}</>
              )}
            </>
          ) : isExpired ? (
            <>
              <strong>{t("twofa_required_title")}</strong> {t("twofa_grace_expired")}
            </>
          ) : (
            <>
              <strong>{t("twofa_enable_prompt")}</strong>{" "}
              {t("twofa_setup_remaining", { count: daysRemaining })}
            </>
          )}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/settings/security"
          className={`
            px-3 py-1 text-sm font-medium rounded-md transition-colors
            ${
              isExpired
                ? "bg-danger-600 text-white hover:bg-danger-700"
                : isUrgent
                  ? "bg-warning-600 text-white hover:bg-warning-700"
                  : "bg-primary-600 text-white hover:bg-primary-700"
            }
          `}
        >
          {t("twofa_setup")}
        </Link>

        {!isExpired && (
          <button
            onClick={() => setDismissed(true)}
            className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            aria-label={t("dismiss_banner")}
          >
            <XMarkIcon className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
          </button>
        )}
      </div>
    </div>
  );
}
